import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, User, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { UniversalCombobox } from '@/components/common';

import { usePersonForm } from '../hooks';
import { PersonNameFields } from '../components/PersonNameFields';
import { AddressFields } from '../components/AddressFields';

export function ActionPersonPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    form,
    isSubmitting,
    onSubmit,
    navigateBack,
    id,
    manage,
  } = usePersonForm();

  return (
    <div className="bg-[#F5F6F7] min-h-screen pb-24">
      <div className="h-[50px] flex items-center justify-between px-4 py-2 border-b border-[#D9D9D9]">
        <p>
          {id ? `${t('actions.edit')} ${t('person')}` : t('newPerson')}
        </p>
        <Button onClick={navigateBack} variant="ghost">
          <X />
        </Button>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => onSubmit(values))}
          className="container w-3/4 mx-auto mt-9 bg-white px-3 py-4 rounded-sm drop-shadow-sm flex flex-col gap-4"
        >
          {/* Person or Company Buttons */}
          <div className="flex gap-16 items-center">
            <div>
              <h1 className="text-xl font-semibold">{t('personDetail')}</h1>
              <div className="mt-2.5 flex gap-2.5">
                <Button
                  type="button"
                  className="bg-[#F0F0F0] border-2 border-button text-black hover:bg-[#F0F0F0]"
                  disabled={!!id}
                >
                  <User className="text-button" />
                  {t('naturalPerson')}
                </Button>
                {!id && (
                  <Button
                    type="button"
                    onClick={() =>
                      navigate(`/app/clients/actionCompany${manage ? `?manage=${manage}` : ''}`)
                    }
                    variant="outline"
                    className="text-black hover:bg-button hover:text-white"
                  >
                    <Building /> {t('legalEntity')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <PersonNameFields control={form.control} errors={form.formState.errors} />

          {/* PINFL */}
          <div className="flex gap-2.5">
            <FormField
              control={form.control}
              name="pinfl"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>{t('pinfl')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Email & Company */}
          <div>
            <h1 className="text-xl font-semibold">{t('email')}</h1>
            <div className="flex gap-2 mt-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('emailAddress')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className={
                          form.formState.errors.email ? 'border-red-500' : 'border-input'
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                name="companyId"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('companyu')}</FormLabel>
                    <FormControl>
                      <UniversalCombobox
                        url="/clients"
                        labelKey="name"
                        valueKey="id"
                        selectedId={field.value}
                        params={{ type: 'COMPANY' }}
                        onSelectItem={(item) => field.onChange(item?.['id'] ?? null)}
                        placeholder={t('selectCompany')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          {/* Phone */}
          <div>
            <h1 className="text-xl font-semibold mb-2">{t('phone')}</h1>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('phoneNumber')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('enterPhone')}
                      className={
                        form.formState.errors.phone ? 'border-red-500' : 'border-input'
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Address */}
          <div>
            <h1 className="text-xl font-semibold mb-3">{t('address')}</h1>
            <AddressFields control={form.control} layout="person" />
          </div>

          {/* Submit Buttons */}
          <div className="mt-6 flex gap-3 justify-end">
            {id ? (
              <Button
                onClick={form.handleSubmit((values) => onSubmit(values, 'update'))}
                type="button"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('saving') : `${t('actions.save')} ${t('person')}`}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={form.handleSubmit((values) => {
                    onSubmit(values, 'createAndContinue');
                  })}
                >
                  {isSubmitting ? t('saving') : t('saveAndCreateAnother')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('saving') : t('actions.save')}
                </Button>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
